import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Box, Typography, CircularProgress } from '@mui/material';
import ProblemForm from '../components/ProblemForm';
import { supabase } from '../lib/supabase';

function ProblemPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [problem, setProblem] = useState({ content: '' });
    const [isLoading, setIsLoading] = useState(!!id);

    useEffect(() => {
        async function fetchProblem() {
            if (id) {
                // Fetch problem with its topics, sources, and techniques
                const { data: problemData, error: problemError } = await supabase
                    .from('problems')
                    .select('*')
                    .eq('id', id)
                    .single();

                if (problemError) {
                    console.error('Error fetching problem:', problemError);
                    setIsLoading(false);
                    return;
                }

                // Fetch problem topics
                const { data: topicsData, error: topicsError } = await supabase
                    .from('problem_topics')
                    .select('topic_id')
                    .eq('problem_id', id);

                if (topicsError) {
                    console.error('Error fetching problem topics:', topicsError);
                } else {
                    problemData.topics = topicsData.map(pt => pt.topic_id);
                }

                // Fetch problem sources
                const { data: sourcesData, error: sourcesError } = await supabase
                    .from('problem_sources')
                    .select('source_id')
                    .eq('problem_id', id);

                if (sourcesError) {
                    console.error('Error fetching problem sources:', sourcesError);
                } else {
                    problemData.sources = sourcesData.map(ps => ps.source_id);
                }

                // Fetch problem techniques
                const { data: techniquesData, error: techniquesError } = await supabase
                    .from('problem_techniques')
                    .select('technique_id')
                    .eq('problem_id', id);

                if (techniquesError) {
                    console.error('Error fetching problem techniques:', techniquesError);
                } else {
                    problemData.techniques = techniquesData.map(pt => pt.technique_id);
                }

                setProblem(problemData);
                setIsLoading(false);
            }
        }

        fetchProblem();
    }, [id]);

    const handleSave = async (updatedProblem) => {
        const { topics, sources, techniques, ...problemData } = updatedProblem;

        console.log('ProblemPage - handleSave called with:', updatedProblem);
        console.log('ProblemPage - extracted topics:', topics);
        console.log('ProblemPage - extracted sources:', sources);
        console.log('ProblemPage - extracted techniques:', techniques);

        try {
            let problemId = id;

            if (id) {
                // Update existing problem
                const { error } = await supabase
                    .from('problems')
                    .update(problemData)
                    .eq('id', id);
                if (error) {
                    console.error('Error updating problem:', error);
                    return;
                }
            } else {
                // Create new problem
                const { data, error } = await supabase
                    .from('problems')
                    .insert(problemData)
                    .select('id')
                    .single();
                if (error) {
                    console.error('Error creating problem:', error);
                    return;
                }
                problemId = data.id;
            }

            // Handle topic relationships
            if (topics && topics.length > 0) {
                // First, delete existing topic relationships for this problem
                await supabase
                    .from('problem_topics')
                    .delete()
                    .eq('problem_id', problemId);

                // Then, insert new topic relationships
                const topicRelationships = topics.map(topicId => ({
                    problem_id: problemId,
                    topic_id: topicId
                }));

                const { error: topicsError } = await supabase
                    .from('problem_topics')
                    .insert(topicRelationships);

                if (topicsError) {
                    console.error('Error saving problem topics:', topicsError);
                }
            } else if (problemId) {
                // If no topics selected, remove all existing relationships
                await supabase
                    .from('problem_topics')
                    .delete()
                    .eq('problem_id', problemId);
            }

            // Handle source relationships
            console.log('ProblemPage - About to handle sources. sources array:', sources);
            if (sources && sources.length > 0) {
                console.log('ProblemPage - Sources exist, proceeding with save');
                // First, delete existing source relationships for this problem
                const { error: deleteError } = await supabase
                    .from('problem_sources')
                    .delete()
                    .eq('problem_id', problemId);

                if (deleteError) {
                    console.error('Error deleting existing problem sources:', deleteError);
                }

                // Then, insert new source relationships
                const sourceRelationships = sources.map(sourceId => ({
                    problem_id: problemId,
                    source_id: sourceId
                }));

                console.log('ProblemPage - About to insert source relationships:', sourceRelationships);
                const { error: sourcesError } = await supabase
                    .from('problem_sources')
                    .insert(sourceRelationships);

                if (sourcesError) {
                    console.error('Error saving problem sources:', sourcesError);
                } else {
                    console.log('ProblemPage - Sources saved successfully');
                }
            } else if (problemId) {
                console.log('ProblemPage - No sources selected, removing existing relationships');
                // If no sources selected, remove all existing relationships
                await supabase
                    .from('problem_sources')
                    .delete()
                    .eq('problem_id', problemId);
            }

            // Handle technique relationships
            console.log('ProblemPage - About to handle techniques. techniques array:', techniques);
            if (techniques && techniques.length > 0) {
                console.log('ProblemPage - Techniques exist, proceeding with save');
                // First, delete existing technique relationships for this problem
                const { error: deleteError } = await supabase
                    .from('problem_techniques')
                    .delete()
                    .eq('problem_id', problemId);

                if (deleteError) {
                    console.error('Error deleting existing problem techniques:', deleteError);
                }

                // Then, insert new technique relationships
                const techniqueRelationships = techniques.map(techniqueId => ({
                    problem_id: problemId,
                    technique_id: techniqueId
                }));

                console.log('ProblemPage - About to insert technique relationships:', techniqueRelationships);
                const { error: techniquesError } = await supabase
                    .from('problem_techniques')
                    .insert(techniqueRelationships);

                if (techniquesError) {
                    console.error('Error saving problem techniques:', techniquesError);
                } else {
                    console.log('ProblemPage - Techniques saved successfully');
                }
            } else if (problemId) {
                console.log('ProblemPage - No techniques selected, removing existing relationships');
                // If no techniques selected, remove all existing relationships
                await supabase
                    .from('problem_techniques')
                    .delete()
                    .eq('problem_id', problemId);
            }

            navigate('/');
        } catch (error) {
            console.error('Error saving problem:', error);
        }
    };

    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Container sx={{ mt: 4 }}>
            <Typography variant="h4" gutterBottom align="center">
                {id ? 'Edit Problem' : 'Add New Problem'}
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <ProblemForm problem={problem} onSave={handleSave} />
            </Box>
        </Container>
    );
}

export default ProblemPage;
